import { once } from '@ember/runloop';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { DateRangeInterface, RecordData, Service, Slack } from 'webapp-types';
import { DateRangeTypeId, ModelName, SnapshotFormat } from 'webapp/enums/enums';

import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import DateRange from 'webapp/models-custom/date-range';
import MetricLimitations from 'webapp/models-custom/metric-limitations';
import type { ScheduleUser } from 'webapp/models-custom/schedule';
import Schedule from 'webapp/models-custom/schedule';
import type BoardShareRecord from 'webapp/models/board-share';
import { DEFAULT_NOTE } from 'webapp/models/board-share';
import type UserRecord from 'webapp/models/user';

import { Severity } from 'webapp/services/logger';

export default class BoardShare extends BufferedModel<
	BoardShareRecord,
	RecordData.BoardShare.Serialized
> {
	@service('constants') declare constantsService: Service.Constants;
	@service('logger') declare loggerService: Service.Logger;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	// ------- props from / to ED record ------

	get id(): string | null {
		return this.model.id;
	}

	get boardId(): number | null {
		return this.getBuffered('boardId');
	}

	set boardId(value: number | null) {
		this.setBuffered('boardId', value);
	}

	get loopId(): number | null {
		return this.getBuffered('loopId');
	}

	set loopId(value: number | null) {
		this.setBuffered('loopId', value);
	}

	get recipients(): string[] {
		return this.getBuffered('recipients');
	}

	set recipients(value: string[]) {
		this.setBuffered('recipients', value);
	}

	get slackChannels(): Slack.Channels {
		return this.getBuffered('slackChannels');
	}

	set slackChannels(value: Slack.Channels) {
		this.setBuffered('slackChannels', value);
	}

	get subject(): string {
		return this.getBuffered('subject');
	}

	set subject(value: string) {
		this.setBuffered('subject', value);
	}

	get note(): string {
		return this.getBuffered('note');
	}

	set note(value: string) {
		this.setBuffered('note', value);
	}

	get format(): number {
		return this.getBuffered('format');
	}

	set format(value: number) {
		this.setBuffered('format', value);
	}

	get createdById(): number {
		return this.model.createdById;
	}

	get created(): number {
		return this.model.created;
	}

	get includeLink(): boolean {
		return this.getBuffered('includeLink');
	}

	set includeLink(value: boolean) {
		this.setBuffered('includeLink', value);
	}

	get scheduleType(): number {
		return this.getBuffered('scheduleType');
	}

	set scheduleType(value: number) {
		this.setBuffered('scheduleType', value);
		this.schedule.type = value;
	}

	get scheduleHourOfDay(): number | null {
		return this.getBuffered('scheduleHourOfDay');
	}

	set scheduleHourOfDay(value: number | null) {
		this.setBuffered('scheduleHourOfDay', value);
		this.schedule.hourOfDay = value;
	}

	get scheduleDayOfWeek(): number[] {
		return this.getBuffered('scheduleDayOfWeek');
	}

	set scheduleDayOfWeek(value: number[]) {
		this.setBuffered('scheduleDayOfWeek', value);
		this.schedule.dayOfWeek = value;
	}

	get scheduleDayOfMonth(): number | null {
		return this.getBuffered('scheduleDayOfMonth');
	}

	set scheduleDayOfMonth(value: number | null) {
		this.setBuffered('scheduleDayOfMonth', value);
		this.schedule.dayOfMonth = value;
	}

	get scheduleUser(): ScheduleUser | null {
		return this.getBuffered('scheduleUser');
	}

	set scheduleUser(value: ScheduleUser | null) {
		this.setBuffered('scheduleUser', value);
		this.schedule.user = value;
	}

	get dateRange(): DateRangeInterface.FormulaInterface | Record<string, never> {
		return this.getBuffered('dateRange');
	}

	set dateRange(value: DateRangeInterface.FormulaInterface | Record<string, never>) {
		this.setBuffered('dateRange', value);
	}

	get includeBgImages(): boolean {
		return this.getBuffered('includeBgImages');
	}

	set includeBgImages(value: boolean) {
		this.setBuffered('includeBgImages', value);
	}

	// ------- other props ------

	get createdBy(): UserRecord | null {
		let user;

		if (this.createdById) {
			user = this.mmUsersService.findRecord(String(this.createdById));
			if (!user) {
				this.loggerService.logError(
					'Missing createdBy for snapshotshare',
					{
						snapshotshareId: this.id,
						userId: this.createdById,
					},
					Severity.WARNING,
				);
			}
		}

		return user || null;
	}

	get isMine() {
		return this.createdById === this.currentUserService.numericId;
	}

	get recipientsText(): string {
		return this.mappedRecipients.map((item) => item.name).join(', ');
	}

	get mappedRecipients() {
		return this.mmUsersService.getMappedRecipients(
			this.recipients,
			[],
			this.slackChannels,
			false,
		);
	}

	get isEmailDelivery(): boolean {
		return this.recipients.length > 0;
	}

	get isSlackDelivery(): boolean {
		return this.slackChannels.length > 0;
	}

	get isNoteDefault() {
		return this.note === DEFAULT_NOTE;
	}

	@cached
	get boardObject(): Board | null {
		let board: Board | null = null;

		const boardId = this.boardId;
		if (boardId) {
			board =
				this.mmBoardsService.normalBoardModels.find(
					(item) => item.id === String(boardId),
				) || null;
		}
		return board;
	}

	@cached
	get loopObject(): BoardLoop | null {
		let loop: BoardLoop | null = null;

		const loopId = this.loopId;
		if (loopId) {
			loop =
				this.mmBoardLoopsService.normalBoardLoopModels.find(
					(item) => item.id === String(loopId),
				) || null;
		}
		return loop;
	}

	get isLoop(): boolean {
		return !!this.loopId;
	}

	get target() {
		// can be null when board shares that have board or loop with private permission and the user is not allowed to see it
		return this.boardObject || this.loopObject;
	}

	_metricLimitations!: MetricLimitations;
	get metricLimitations(): MetricLimitations {
		if (!this._metricLimitations) {
			let formula = this.dateRange;
			if (!formula.range) {
				formula = DateRange.predefinedRanges.find(
					(item) => item.rangeTypeId === DateRangeTypeId.PREVIOUS_MONTH,
				)!.formula;
			}

			const dateRange = DateRange.createFromFormula(
					formula as DateRangeInterface.FormulaInterface,
				),
				limitations = new MetricLimitations({ dateRange });

			this._metricLimitations = limitations;
		}

		return this._metricLimitations;
	}

	get createdByText(): string {
		return this.createdBy?.name || '/';
	}

	get titleForSlackList(): string {
		return this.boardObject?.name || '';
	}

	get slackChannelCountForSlackList(): number {
		return this.slackChannels.length;
	}

	get deliveryOptionsCount(): number {
		let count = 0;

		if (this.recipients.length) {
			count++;
		}

		if (this.slackChannels.length) {
			count++;
		}

		return count;
	}

	get recipientsCount(): number {
		return this.mappedRecipients.length;
	}

	get deliveryTimeLevel(): number {
		return this.schedule.deliveryTimeLevel;
	}

	get isScheduledForMe(): boolean {
		return this.recipients.includes(this.currentUserService.model?.email || '');
	}

	getDataForClone() {
		const data = this.toJSON();

		delete data.created;
		delete data.createdById;

		return data;
	}

	getTrackingDataForCreate(origin: string) {
		const deliveryTextsForTracking = [];
		if (this.isEmailDelivery) {
			deliveryTextsForTracking.push('email');
		}
		if (this.isSlackDelivery) {
			deliveryTextsForTracking.push('slack');
		}

		return {
			format: this.format === SnapshotFormat.PDF ? 'pdf' : 'jpg',
			type: this.isLoop ? 'loop' : 'databoard',
			delivery: deliveryTextsForTracking.join(', '),
			frequency: this.schedule.typeText.toLowerCase(),
			origin,
			name: this.target?.name,
			datasources: this.target?.specificSourceTypesKeys,
		};
	}

	// --- schedule ---

	declare _schedule: Schedule;

	get schedule(): Schedule {
		if (this._schedule) {
			// mark dependents so this getter updates and triggers the schedule update from record
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.scheduleType;
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.scheduleHourOfDay;
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.scheduleDayOfWeek;
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.scheduleDayOfMonth;
			once(this._schedule, this._schedule.updateScheduleFromModel, this);
		} else {
			this._schedule = Schedule.createFromModel(this);
		}
		return this._schedule;
	}

	updateScheduleValues(): void {
		const schedule = this.schedule;
		schedule.updateScheduleValues();
		schedule.updateModelFromSchedule(this);
	}

	cleanupUnusedScheduleValues(): void {
		const schedule = this.schedule;
		schedule.cleanupUnusedScheduleValues();
		schedule.updateModelFromSchedule(this);
	}

	createAnotherModel() {
		return new BoardShare({ model: this.model });
	}

	static override modelName = ModelName.BOARD_SHARE; // store record type
}
